import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import FanpictorLogo from "@/components/FanpictorLogo";
import MenuBurger from "@/components/MenuBurger";
import { useContactModal } from "@/components/ContactModal";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SiteHeaderProps {
  showMenu?: boolean;
  className?: string;
}

/**
 * Fixed global header used across the Fanpictor platform.
 * Stays pinned at the top of the viewport on every page.
 */
const SiteHeader = ({ showMenu = true, className = "" }: SiteHeaderProps) => {
  const { open } = useContactModal();
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 flex items-center justify-between bg-background px-5 py-3 md:px-10 md:py-5 ${className}`}
    >
      <FanpictorLogo />
      <div className="flex items-center gap-2 md:gap-6">
        {/* Contact button: desktop/tablet only — collapsed into burger menu on mobile */}
        <button
          onClick={open}
          className="hidden border border-white/55 bg-transparent px-4 py-2 font-body text-[11px] uppercase tracking-[0.2em] text-white transition-all hover:border-white hover:bg-white/10 md:inline-block md:px-5"
          type="button"
        >
          Get in Touch
        </button>
        {/* Logout: desktop/tablet only — collapsed into burger menu on mobile */}
        {session && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleLogout}
                aria-label="Log out"
                title="Log out"
                className="hidden h-10 w-10 items-center justify-center text-white/70 transition-colors hover:text-white md:flex"
                type="button"
              >
                <LogOut className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-popover text-popover-foreground">
              <span className="text-xs uppercase tracking-wider">Log out</span>
            </TooltipContent>
          </Tooltip>
        )}
        {showMenu && <MenuBurger />}
      </div>
    </header>
  );
};

/** Reserve vertical space equal to the fixed SiteHeader so content does not sit under it. */
export const SiteHeaderSpacer = ({ className = "" }: { className?: string }) => (
  <div aria-hidden className={`h-[60px] md:h-[88px] shrink-0 ${className}`} />
);

export default SiteHeader;
